import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import useFormValidatorText from '../../../hooks/validators/use-form-validator-text';
import AuthService from '../../../services/use_case/auth/AuthService';

type ResetPasswordInputs = {
    password: string;
    password_confirmation: string;
};

const IconEyeOpen = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const IconEyeClosed = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const ResetPassword: React.FC = () => {

    const [t] = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const email = params.get('email');

    const { handleSubmit, register, errors, watch } = useForm<ResetPasswordInputs>();
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const passwordValue = watch('password');

    const textValidator = useFormValidatorText();

    const passwordRegister = register(textValidator);
    const confirmRegister = register({
        ...textValidator,
        validate: (value: string) => value === passwordValue || t('The passwords do not match').toString(),
    });

    if (!token || !email) {
        history.replace('/forgot-password');
        return null;
    }

    const _handleOnSubmit = async (data: ResetPasswordInputs) => {
        setIsLoading(true);
        try {
            await new AuthService().resetPassword(token, email, data.password, data.password_confirmation);
            setIsSuccess(true);
        } catch {
            // errors handled by BaseService via iziToast
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth">
            <div className="box-center">
                <div className="auth__box">
                    <div className="row">

                        <div className="col-12 col-md-6">
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <img className="auth__logo" src={process.env.REACT_APP_AUTH_LOGO_URI || '/img/logo-cactus.png'} alt="logo" />
                            </div>
                        </div>

                        <div className="col-12 col-md-6 auth__form">
                            <p className="auth__heading">{t('reset_password_heading')}</p>
                            <p className="auth__subheading">{t('reset_password_subtitle')}</p>

                            {isSuccess ? (
                                <p className="auth__success-msg">{t('reset_password_success')}</p>
                            ) : (
                                <form onSubmit={handleSubmit(_handleOnSubmit)}>

                                    <label className="w-100">
                                        <span className="form__label">{t('New password')}</span>
                                        <div className="auth__input-wrapper mt-2">
                                            <input
                                                id="password"
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="*****"
                                                autoComplete="new-password"
                                                ref={passwordRegister}
                                                className="w-100 form-control--dark"
                                            />
                                            <button
                                                type="button"
                                                className="auth__password-toggle"
                                                onClick={() => setShowPassword(v => !v)}
                                                tabIndex={-1}
                                                aria-label={showPassword ? t('hide_password') : t('show_password')}
                                            >
                                                {showPassword ? <IconEyeClosed /> : <IconEyeOpen />}
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <div className="mt-2 form__invalid-feedback font-weight-bold">
                                                {errors.password.message}
                                            </div>
                                        )}
                                    </label>

                                    <div className="mt-2">
                                        <label className="w-100">
                                            <span className="form__label">{t('Confirm password')}</span>
                                            <div className="auth__input-wrapper mt-2">
                                                <input
                                                    id="password_confirmation"
                                                    name="password_confirmation"
                                                    type={showConfirm ? 'text' : 'password'}
                                                    placeholder="*****"
                                                    autoComplete="new-password"
                                                    ref={confirmRegister}
                                                    className="w-100 form-control--dark"
                                                />
                                                <button
                                                    type="button"
                                                    className="auth__password-toggle"
                                                    onClick={() => setShowConfirm(v => !v)}
                                                    tabIndex={-1}
                                                    aria-label={showConfirm ? t('hide_password') : t('show_password')}
                                                >
                                                    {showConfirm ? <IconEyeClosed /> : <IconEyeOpen />}
                                                </button>
                                            </div>
                                            {errors.password_confirmation && (
                                                <div className="mt-2 form__invalid-feedback font-weight-bold">
                                                    {errors.password_confirmation.message}
                                                </div>
                                            )}
                                        </label>
                                    </div>

                                    <div className="d-flex justify-content-end mt-2">
                                        <button
                                            type="submit"
                                            className="btn btn-cactus"
                                            disabled={isLoading}
                                        >
                                            {isLoading
                                                ? <span className="auth__btn-spinner" />
                                                : t('reset_password_submit')
                                            }
                                        </button>
                                    </div>

                                </form>
                            )}

                            {isSuccess && (
                                <p className="auth__register-hint">
                                    <Link to="/login">{t('forgot_password_back_login')}</Link>
                                </p>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );

};

export default React.memo(ResetPassword);
